// @ts-check
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { tolerantId } from "./firestoreHelpers";

/**
 * Consultas que fijan el edificio (Fase 0D).
 *
 * Con las reglas 0D una consulta de lista solo pasa si sus filtros prueban la
 * regla. payments, expensesapartments, expenses y amenitycharges se leen por
 * el edificio del doc (buildingid), así que toda consulta por otro campo
 * (montlyaccountId, lockedByCierreId, expenseId, projectId, unitId…) suma
 * where("buildingid", "in", tolerantId(bid)). Si no hay forma (búsqueda por id
 * de documento), se lee doc por doc con getDoc: la regla evalúa el doc real.
 */

/**
 * true si el error es un permiso negado por las reglas de Firestore.
 *
 * @param {unknown} e - Error atrapado
 */
export function esPermisoDenegado(e) {
  const code = String((e && /** @type {any} */ (e).code) || "");
  return code === "permission-denied" || code === "firestore/permission-denied";
}

/**
 * Edificio en forma canónica, igual que bidKey de las reglas y normBid del
 * servidor: 10, "10" y "010" dan "10".
 *
 * @param {string | number | null | undefined} bid - Edificio
 */
export function bidCanonico(bid) {
  const n = Number(bid);
  return Number.isInteger(n) && String(bid ?? "").trim() !== "" ? String(n) : String(bid ?? "");
}

/**
 * Filtro por edificio tolerante a number/string ([10, "10"]).
 *
 * @param {string | number} buildingId - Edificio
 * @param {string} [campo] - Campo del edificio en la colección (buildingid por defecto)
 */
export const delEdificio = (buildingId, campo = "buildingid") => where(campo, "in", tolerantId(buildingId));

/**
 * Docs de un cierre (payments y expensesapartments por montlyaccountId,
 * amenitycharges por lockedByCierreId), siempre acotados al edificio del
 * cierre: funciona con ids de cierre viejos (aleatorios) y nuevos, y deja
 * afuera docs de otro edificio que apunten al mismo cierre.
 *
 * @param {import("firebase/firestore").Firestore} db - Instancia de Firestore
 * @param {string} coleccion - payments | expensesapartments | amenitycharges
 * @param {string} cierreId - Id del doc montlyaccount
 * @param {string | number} buildingId - Edificio del cierre
 * @param {string} [campo] - Campo que guarda el id del cierre (montlyaccountId por defecto)
 */
export function consultaDelCierre(db, coleccion, cierreId, buildingId, campo = "montlyaccountId") {
  return query(collection(db, coleccion), where(campo, "==", cierreId), delEdificio(buildingId));
}

/**
 * Vigilancia de entrega de un WhatsApp saliente (contrato C3): el doc de
 * messages por su metaMessageId, acotado al edificio (campo buildingId, que
 * el servidor graba en los salientes donde lo conoce).
 *
 * @param {import("firebase/firestore").Firestore} db - Instancia de Firestore
 * @param {string} metaMessageId - Id del mensaje en Meta
 * @param {string | number} buildingId - Edificio desde el que se envió
 */
export function consultaEntregaWhatsApp(db, metaMessageId, buildingId) {
  return query(
    collection(db, "messages"),
    where("metaMessageId", "==", metaMessageId),
    delEdificio(buildingId, "buildingId")
  );
}

const TANDA_LECTURAS = 50;

/**
 * Lee docs por id con getDoc, en el orden pedido y sin repetidos. Los que no
 * existen o la regla niega se omiten, igual que hacía where("__name__", "in"):
 * un gasto borrado no tiene doc y la regla, sin edificio que mirar, lo niega.
 * Cualquier otro error (red, cuota) se propaga.
 *
 * @param {import("firebase/firestore").Firestore} db - Instancia de Firestore
 * @param {string} coleccion - Colección
 * @param {Array<string | number | null | undefined>} ids - Ids de documento
 * @returns {Promise<Array<Record<string, any> & { id: string }>>}
 */
export async function leerDocsPorId(db, coleccion, ids) {
  const unicos = [...new Set((ids || []).filter((id) => id != null && id !== "").map(String))];
  const leidos = [];
  for (let i = 0; i < unicos.length; i += TANDA_LECTURAS) {
    const tanda = unicos.slice(i, i + TANDA_LECTURAS);
    const snaps = await Promise.all(
      tanda.map(async (id) => {
        try {
          return await getDoc(doc(db, coleccion, id));
        } catch (e) {
          if (esPermisoDenegado(e)) return null;
          throw e;
        }
      })
    );
    snaps.forEach((s) => {
      if (s && s.exists()) leidos.push({ id: s.id, ...s.data() });
    });
  }
  return leidos;
}

/**
 * Gastos por id de documento (liquidatedExpenseIds, expensesapartments.expenseId).
 *
 * @param {import("firebase/firestore").Firestore} db - Instancia de Firestore
 * @param {Array<string | number | null | undefined>} ids - Ids de documento de expenses
 */
export const leerGastosPorId = (db, ids) => leerDocsPorId(db, "expenses", ids);

/**
 * user_access/{uid} de la propia cuenta: los permisos que calcula el servidor
 * (userids, staffBids, residentBids, formerBids, superadmin…). La regla deja
 * leer solo el propio. null si no hay sesión, si todavía no existe o si la
 * lectura se niega.
 *
 * @param {import("firebase/firestore").Firestore} db - Instancia de Firestore
 * @param {string | null | undefined} uid - uid de la sesión
 * @returns {Promise<Record<string, any> | null>}
 */
export async function leerMiAcceso(db, uid) {
  if (!uid) return null;
  try {
    const snap = await getDoc(doc(db, "user_access", String(uid)));
    return snap.exists() ? snap.data() || {} : null;
  } catch (e) {
    if (esPermisoDenegado(e)) return null;
    throw e;
  }
}

/**
 * Edificios que administra la cuenta (bids canónicos de staffBids), para acotar
 * "Ver como residente" a lo que las reglas le dejan leer: una consulta `in` con
 * un edificio ajeno se niega entera. null = sin filtro (superadmin, o cuenta
 * todavía sin user_access: se consulta como antes).
 *
 * @param {Record<string, any> | null} acceso - user_access de quien mira
 * @returns {Set<string> | null}
 */
export function edificiosQueAdministra(acceso) {
  if (!acceso || acceso.superadmin === true) return null;
  const bids = Array.isArray(acceso.staffBids) ? acceso.staffBids : [];
  return new Set(bids.map(bidCanonico));
}

// Variantes de userid por consulta: × 2 variantes del edificio ([10, "10"]) =
// 28 disyunciones, debajo del tope de 30 de Firestore.
const VARIANTES_USERID_POR_CONSULTA = 14;

/**
 * Pagos de un edificio para las pantallas de residente (Expensas, Cuentas,
 * Mis pagos).
 *
 * El residente vigente (o el staff en "Ver como residente") lee todos los
 * pagos del edificio y la pantalla filtra en el cliente (los propios, los del
 * titular si es secundario, los de sus inquilinos). El ex-ocupante solo puede
 * leer los SUYOS (reglas 0D) y una consulta que no fija el userid se le niega
 * entera: si la consulta por edificio se niega, se repite fijando también los
 * userid de la cuenta (user_access.userids).
 *
 * @param {import("firebase/firestore").Firestore} db - Instancia de Firestore
 * @param {string | number} buildingId - Edificio
 * @param {{ uid?: string | null }} [opciones] - uid de la sesión; sin uid (p. ej.
 *   en "Ver como residente") no hay segundo intento y el error se propaga
 * @returns {Promise<Array<Record<string, any> & { id: string }>>}
 */
export async function leerPagosParaResidente(db, buildingId, { uid = null } = {}) {
  const pagos = collection(db, "payments");
  try {
    const snap = await getDocs(query(pagos, delEdificio(buildingId)));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) {
    if (!uid || !esPermisoDenegado(e)) throw e;
    const acceso = await leerMiAcceso(db, uid);
    const propios = Array.isArray(acceso?.userids) ? acceso.userids : [];
    const porClave = new Map();
    propios.flatMap((id) => tolerantId(id)).forEach((v) => porClave.set(`${typeof v}:${v}`, v));
    const variantes = [...porClave.values()];
    if (variantes.length === 0) throw e;
    const porId = new Map();
    for (let i = 0; i < variantes.length; i += VARIANTES_USERID_POR_CONSULTA) {
      const tanda = variantes.slice(i, i + VARIANTES_USERID_POR_CONSULTA);
      const snap = await getDocs(query(pagos, delEdificio(buildingId), where("userid", "in", tanda)));
      snap.docs.forEach((d) => porId.set(d.id, { id: d.id, ...d.data() }));
    }
    return [...porId.values()];
  }
}
